import { zodResolver } from '@hookform/resolvers/zod';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ControlledField,
  LanguageOptions,
  PhoneInputField,
  SelectInputField,
  removeEmptyStringKeys,
} from 'remote/global';
import { Button } from '@mui/material';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { hasReadOnlyPermission } from 'utils/helpers';
import { usePutOperatorsDriversMutation } from 'api/queries/operators';
import { DriverDtoSchema } from '../BookingFormFactory/types';
import { DriverDetailsSchemaType, driverDetailsSchema } from './types';

const LANGUAGE_OPTIONS = [
  LanguageOptions.et,
  LanguageOptions.en,
  LanguageOptions.lv,
  LanguageOptions.ru,
];

// TODO: Implement disabled state after Base TextField fix
export function DriverDetailsFormFields({
  form,
  focusInitial,
}: {
  form: UseFormReturn<DriverDetailsSchemaType>;
  focusInitial?: boolean;
}) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const { control } = form;
  return (
    <>
      <ControlledField
        control={control}
        name='contactInfo.firstName'
        translationKey='firstName'
        fieldProps={{ InputProps: { autoComplete: 'given-name' } }}
        focusInitial={focusInitial}
      />
      <ControlledField
        control={control}
        name='contactInfo.lastName'
        translationKey='lastName'
        fieldProps={{ InputProps: { autoComplete: 'family-name' } }}
      />
      <ControlledField control={control} name='employmentContractNo' />
      <ControlledField control={control} name='idCode' />
      <ControlledField
        control={control}
        name='contactInfo.email'
        translationKey='email'
        fieldProps={{
          InputProps: {
            type: 'email',
            autoComplete: 'email',
          },
        }}
      />
      <ControlledField
        control={control}
        name='contactInfo.phone'
        render={(renderProps) => <PhoneInputField {...renderProps} translationKey='phone' />}
      />
      <ControlledField
        control={control}
        name='languages'
        render={(renderProps) => (
          <SelectInputField
            {...renderProps}
            fieldProps={{
              multiple: true,
            }}
            options={LANGUAGE_OPTIONS.map((lang) => ({
              label: t(`Language.${lang}`),
              value: lang,
            }))}
          />
        )}
      />
    </>
  );
}

function DriverDetailsForm({
  defaultValues,
  driverId,
}: {
  defaultValues?: DriverDtoSchema;
  driverId: string;
}) {
  const { t } = useTranslation();
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo ?? {};
  const formInstance = useForm<DriverDetailsSchemaType>({
    defaultValues: {
      contactInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      employmentContractNo: '',
      idCode: '',
      languages: [],
      ...defaultValues,
    },
    resolver: zodResolver(driverDetailsSchema),
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const putOperatorsDriversMutation = usePutOperatorsDriversMutation();

  const onValid = async (data: DriverDetailsSchemaType) => {
    const payload: DriverDtoSchema = removeEmptyStringKeys({
      id: driverId,
      ...data,
    });

    await putOperatorsDriversMutation.mutateAsync(payload);
  };

  const isFormDisabled =
    isLoading ||
    isSubmitting ||
    isValidating ||
    hasReadOnlyPermission(permissions?.DRIVER_FILE ?? '');

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <DriverDetailsFormFields form={formInstance} />
      <Button
        type='submit'
        variant='contained'
        disabled={isFormDisabled}
        fullWidth
        sx={{ mt: '2rem' }}
      >
        {t('save')}
      </Button>
    </form>
  );
}

export default DriverDetailsForm;
